export interface SearchableProperty {
  title?: string;
  type?: string;
  room_number?: string;
  full_address?: string;
  street?: string;
  ward_name?: string;
  province_name?: string;
  address?: string | Record<string, unknown>;
  status?: string;
}

export const removeVietnameseTones = (text: string): string => {
  return text
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/đ/g, 'd')
    .replace(/Đ/g, 'D')
    .normalize('NFC');
};

export const matchesPropertySearch = (property: SearchableProperty, query: string): boolean => {
  const normalizedQuery = removeVietnameseTones(query.trim().toLowerCase());

  if (!normalizedQuery) {
    return true;
  }

  const searchableFields = [
    property.title,
    property.type,
    property.room_number,
    property.full_address,
    property.street,
    property.ward_name,
    property.province_name,
    typeof property.address === 'string' ? property.address : ''
  ]
    .filter(Boolean)
    .join(' ')
    .toLowerCase();

  return removeVietnameseTones(searchableFields).includes(normalizedQuery);
};

export const filterPropertiesBySearchQuery = <T extends SearchableProperty>(
  properties: T[],
  query: string
): T[] => {
  const normalizedQuery = query.trim();

  if (!normalizedQuery) {
    return properties;
  }

  return properties.filter((property) => matchesPropertySearch(property, normalizedQuery));
};

export const filterPropertiesBySearchQueryAndStatus = <T extends SearchableProperty>(
  properties: T[],
  query: string,
  statusFilter: 'all' | 'available' | 'rented' | 'pending' = 'all'
): T[] => {
  const filteredByStatus = properties.filter((property) => {
    if (statusFilter === 'all') return true;
    const status = String(property.status || '').toLowerCase();
    if (statusFilter === 'available') {
      return status === 'available' || status === 'active' || status === 'vacant';
    }
    if (statusFilter === 'pending') {
      return status === 'pending' || status === 'draft';
    }
    return status === 'rented' || status === 'occupied' || status === 'completed' || status === 'closed';
  });

  return filterPropertiesBySearchQuery(filteredByStatus, query);
};

export const filterPropertiesBySearchQueryAndStatusAndList = <T extends SearchableProperty>(
  properties: T[],
  query: string,
  statusFilter: 'all' | 'available' | 'rented' | 'pending' = 'all',
  selectedPropertyIds: number[] | null = null
): T[] => {
  const filteredByList = selectedPropertyIds && selectedPropertyIds.length > 0
    ? properties.filter((property) => selectedPropertyIds.includes(Number(property.id)))
    : properties;

  return filterPropertiesBySearchQueryAndStatus(filteredByList, query, statusFilter);
};
